import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const frameworkPrompts = {
  "10-10-10": [
    "How will you feel about this decision in 10 minutes?",
    "How will you feel about this decision in 10 months?",
    "How will you feel about this decision in 10 years?"
  ],
  "inversion": [
    "What would make this decision a complete failure?",
    "What conditions would cause those failure modes?",
    "How would you prevent or avoid those causes?"
  ],
  "regret_minimization": [
    "At 80, what might you regret about taking this path?",
    "At 80, what might you regret about NOT taking this path?",
    "Which regret weighs more heavily?"
  ],
  "opportunity_cost": [
    "What opportunities do you gain with Option A? What do you lose?",
    "What opportunities do you gain with Option B? What do you lose?",
    "Which set of trade-offs aligns better with your priorities?"
  ],
  "risk_reversibility": [
    "What's the worst realistic outcome? How bad is it really?",
    "How reversible is this decision? What's the cost to undo it?",
    "Given the risk and reversibility, what's your risk-adjusted view?"
  ],
  "first_principles": [
    "What are the fundamental facts here? Strip away assumptions.",
    "What are you assuming that might not be true?",
    "Rebuilding from facts alone, what does logic suggest?"
  ],
  "second_order": [
    "What are the immediate, first-order effects of this decision?",
    "What happens next? What do those effects cause?",
    "Are the second-order effects acceptable? Worth the first-order gains?"
  ],
  "worst_best_case": [
    "What's the realistic worst case? Not catastrophizing—genuinely bad.",
    "What's the realistic best case? Not fantasy—genuinely good.",
    "What's the most likely outcome? Where does it fall?"
  ]
};

const frameworkNames = {
  "10-10-10": "10-10-10 Rule",
  "inversion": "Inversion Thinking",
  "regret_minimization": "Regret Minimization",
  "opportunity_cost": "Opportunity Cost",
  "risk_reversibility": "Risk vs Reversibility",
  "first_principles": "First Principles",
  "second_order": "Second-Order Effects",
  "worst_best_case": "Worst/Best Case Mapping"
};

export default function ThinkingFlowPage() {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [currentStep, setCurrentStep] = useState(null);
  const [response, setResponse] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadSession = async () => {
      try {
        const sessionRes = await axios.get(`${API}/decision/${sessionId}`);
        setSession(sessionRes.data);
        
        const stepNumber = sessionRes.data.current_step || 0;
        
        if (sessionRes.data.status === "completed") {
          navigate(`/map/${sessionId}`);
          return;
        }
        
        const prompts = frameworkPrompts[sessionRes.data.framework] || frameworkPrompts["10-10-10"];
        
        setCurrentStep({
          step_number: stepNumber + 1,
          total_steps: 3,
          prompt: prompts[stepNumber] || "",
          is_complete: false
        });
      } catch {
        toast.error("Session not found");
        navigate("/");
      } finally {
        setIsLoading(false);
      }
    };

    loadSession();
  }, [sessionId, navigate]);

  const handleSubmit = async () => {
    if (!response.trim() || response.trim().length < 20) {
      toast.error("Please write a bit more to continue");
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await axios.post(`${API}/decision/${sessionId}/think`, {
        user_response: response.trim()
      });

      if (result.data.is_complete) {
        navigate(`/map/${sessionId}`);
      } else {
        // Load next step prompt
        const prompts = frameworkPrompts[session.framework] || frameworkPrompts["10-10-10"];
        const nextStepNum = result.data.current_step?.step_number || (currentStep.step_number + 1);
        
        setCurrentStep({
          step_number: nextStepNum,
          total_steps: 3,
          prompt: prompts[nextStepNum - 1] || "",
          is_complete: false
        });
        setResponse("");
      }
    } catch {
      toast.error("Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-2xl mx-auto px-6 h-14 flex items-center justify-between">
          <button
            data-testid="back-btn"
            onClick={() => navigate(`/framework/${sessionId}`)}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <span className="text-xs text-muted-foreground">
            {frameworkNames[session?.framework] || session?.framework}
          </span>
        </div>
      </header>

      {/* Progress */}
      {currentStep && (
        <div className="border-b border-border/30 bg-background">
          <div className="max-w-2xl mx-auto px-6 py-4">
            <div className="flex items-center justify-center gap-2">
              {Array.from({ length: currentStep.total_steps }).map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 w-12 rounded-full transition-all ${
                    i + 1 < currentStep.step_number
                      ? 'bg-primary/60'
                      : i + 1 === currentStep.step_number
                      ? 'bg-primary'
                      : 'bg-border'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 px-6 py-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep?.step_number}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.3 }}
            className="max-w-xl mx-auto space-y-8"
          >
            {/* Step indicator */}
            <div className="text-center">
              <p className="text-xs font-medium text-primary uppercase tracking-wider">
                Step {currentStep?.step_number} of {currentStep?.total_steps}
              </p>
            </div>

            {/* Prompt */}
            <h1 className="font-heading text-2xl md:text-3xl text-foreground text-center leading-snug">
              {currentStep?.prompt}
            </h1>

            {/* Input */}
            <div className="floating-card p-6">
              <textarea
                data-testid="thinking-response"
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                placeholder="Think through this honestly. Write what comes to mind..."
                className="w-full bg-transparent border-none outline-none resize-none text-foreground placeholder:text-muted-foreground/40 leading-relaxed min-h-[180px]"
                autoFocus
              />
            </div>

            {/* Submit */}
            <div className="flex justify-end">
              <Button
                data-testid="submit-thinking-btn"
                onClick={handleSubmit}
                disabled={isSubmitting || !response.trim()}
                className="h-11 px-6 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 text-sm shadow-lg shadow-primary/20"
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                ) : currentStep?.step_number === currentStep?.total_steps ? (
                  <>
                    Complete
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Decision reminder */}
      {session && (
        <footer className="border-t border-border/30 px-6 py-4 bg-background">
          <div className="max-w-xl mx-auto">
            <p className="text-xs text-muted-foreground text-center truncate">
              {session.decision_text}
            </p>
          </div>
        </footer>
      )}

      {/* Disclaimer Footer */}
      <div className="py-4 px-6">
        <p className="text-[11px] text-muted-foreground/50 text-center">
          Decisora provides structured thinking tools, not professional advice.
        </p>
      </div>
    </div>
  );
}
